/**
 * Movimiento lateral entre canales (mesas) del mismo estado de producto.
 * No debe recalcular caducidad ni print_date.
 */
function normalizeStateId(stateId) {
    if (stateId == null || stateId === '') {
        return ''
    }
    return String(stateId)
}

export function isChannelOnlyMove(lastState, nextStateBase, previousChannel, nextChannel) {
    if (normalizeStateId(lastState) !== normalizeStateId(nextStateBase)) {
        return false
    }
    if (previousChannel == null || nextChannel == null) {
        return false
    }
    return parseInt(previousChannel, 10) !== parseInt(nextChannel, 10)
}

export function isChannelMoveColumns(currentState, nextState) {
    return Boolean(currentState && currentState.channel && nextState && nextState.channel)
}

function getMinStateOrder(productStates) {
    let minOrder = Infinity
    if (!productStates) {
        return NaN
    }
    Object.keys(productStates).forEach((id) => {
        const order = parseInt(productStates[id].order, 10)
        if (!isNaN(order)) {
            minOrder = Math.min(minOrder, order)
        }
    })
    return minOrder === Infinity ? NaN : minOrder
}

function stateBaseId(columnOrStateId) {
    if (columnOrStateId == null || columnOrStateId === '') {
        return columnOrStateId
    }
    const s = String(columnOrStateId)
    // Solo quitar sufijo _channelN; no usar split('_')[0] (truncaría IDs como abc_def).
    return s.includes('_channel') ? s.split('_channel')[0] : s
}

/**
 * ¿Preservar fechas al mover? Sí entre canales/mesas paralelas (p. ej. Estado 2 ↔ Estado 3).
 * No al salir del primer estado del flujo (p. ej. Estado 1 → cualquier destino).
 *
 * @param {Object|null} currentState - columna origen del tablero
 * @param {Object|null} nextState - columna destino
 * @param {Object|null} timer - timer que se mueve
 * @param {Object|null|undefined} productStates - product.states
 */
export function shouldPreserveTimerDatesOnMove(currentState, nextState, timer, productStates) {
    if (!currentState || !nextState || !timer) {
        return false
    }
    if (String(currentState.id) === String(nextState.id)) {
        return false
    }

    const timerStateBase = stateBaseId(timer.state)
    const minOrder = getMinStateOrder(productStates)
    const timerStateOrder = productStates && productStates[timerStateBase]
        ? parseInt(productStates[timerStateBase].order, 10)
        : NaN

    const currentStateBase = stateBaseId(currentState.id)
    const nextStateBase = stateBaseId(nextState.id)
    const currentOrder = productStates && productStates[currentStateBase]
        ? parseInt(productStates[currentStateBase].order, 10)
        : parseInt(currentState.order, 10)
    const nextOrder = productStates && productStates[nextStateBase]
        ? parseInt(productStates[nextStateBase].order, 10)
        : parseInt(nextState.order, 10)

    // Primer estado del flujo (Estado 1): recalcular siempre al mover hacia otro sitio
    if (!isNaN(minOrder) && !isNaN(timerStateOrder) && timerStateOrder === minOrder) {
        return false
    }

    // Retroceso en el flujo: lógica habitual del store (timeout, caducidad, etc.)
    if (!isNaN(nextOrder) && !isNaN(currentOrder) && nextOrder < currentOrder) {
        return false
    }

    // Canales explícitos (_channelN) del mismo estado base
    if (isChannelMoveColumns(currentState, nextState)) {
        if (currentStateBase === nextStateBase) {
            return true
        }
    }

    // Columnas paralelas con el mismo order en tablero
    const currentColOrder = parseInt(currentState.order, 10)
    const nextColOrder = parseInt(nextState.order, 10)
    if (!isNaN(currentColOrder) && !isNaN(nextColOrder) && currentColOrder === nextColOrder) {
        return true
    }

    // Desde Estado 2+ hacia adelante (p. ej. Estado 2 → Estado 3 como mesas/canales)
    if (!isNaN(timerStateOrder) && !isNaN(minOrder) && timerStateOrder > minOrder) {
        return true
    }

    return false
}

/**
 * Movimiento lateral puro: mismo estado base, solo cambia el canal (_channelN).
 */
export function isLateralChannelOnlyMove(currentState, nextState, timer, productStates) {
    if (!isChannelMoveColumns(currentState, nextState) || !timer) {
        return false
    }
    return stateBaseId(currentState.id) === stateBaseId(nextState.id)
        && String(currentState.id) !== String(nextState.id)
}

function pickTimestamp(data, key) {
    const ts = data && data[key]
    if (ts == null || ts === '') {
        return null
    }
    const n = typeof ts === 'string' ? parseInt(ts, 10) : ts
    return isNaN(n) ? null : n
}

const DATE_FIELD_PREFIXES = ['print_date', 'prep_date', 'date']

const EXTRA_SNAPSHOT_KEYS = [
    'caducidad_estado_inicial_timestamp',
    'caducidad_ultimo_estado_timestamp',
    'last_move_timestamp',
    'currenttime',
    'currenttime_timestamp',
    'currenttime_day',
    'currenttime_time',
    'currentday',
    'currentday_timestamp',
    'currentday_day',
    'currentday_time'
]

function isDateRelatedField(key) {
    return DATE_FIELD_PREFIXES.some((prefix) => key === prefix || key.startsWith(`${prefix}_`))
}

/**
 * Copia todos los campos de fecha/caducidad para restaurarlos tras el API.
 */
export function captureTimerDateSnapshot(data) {
    if (!data) {
        return {}
    }
    const snap = {}
    Object.keys(data).forEach((key) => {
        if (isDateRelatedField(key) || EXTRA_SNAPSHOT_KEYS.includes(key)) {
            snap[key] = data[key]
        }
    })
    return snap
}

/**
 * Restaura campos de fecha/caducidad desde un snapshot previo al move.
 */
export function applyTimerDateSnapshot(data, snapshot) {
    if (!data || !snapshot) {
        return data
    }
    Object.assign(data, snapshot)
    return data
}

/**
 * Alinea strings de fecha (con segundos) con sus timestamps unix.
 * Evita deriva cuando el backend o formatos sin segundos truncan la hora.
 */
export function syncTimerDateStringsFromTimestamps(data) {
    if (!data) {
        return data
    }

    DATE_FIELD_PREFIXES.forEach((prefix) => {
        const ts = pickTimestamp(data, `${prefix}_timestamp`)
        if (ts == null) {
            return
        }
        const m = moment.unix(ts)
        data[prefix] = m.format('DD/MM/YYYY HH:mm:ss')
        data[`${prefix}_day`] = m.format('DD/MM/YYYY')
        data[`${prefix}_time`] = m.format('H:mm')
        for (let i = 1; i <= 7; i++) {
            data[`${prefix}_d${i}`] = m.isoWeekday() === i ? 'x' : ' '
        }
    })

    const currentTs = pickTimestamp(data, 'currenttime_timestamp')
    if (currentTs != null) {
        const m = moment.unix(currentTs)
        data.currenttime = m.format('DD/MM/YYYY HH:mm:ss')
        data.currenttime_day = m.format('DD/MM/YYYY')
        data.currenttime_time = m.format('H:mm')
    }

    return data
}

/**
 * Payload de fechas para enviar al backend en move/update entre canales.
 */
export function getTimerDateFieldsForPayload(data) {
    if (!data) {
        return {}
    }
    const working = { ...data }
    syncTimerDateStringsFromTimestamps(working)
    return captureTimerDateSnapshot(working)
}

/**
 * Fusiona campos de fecha de un patch parcial en timer.data local.
 */
export function applyTimerDateFieldsFromPatch(target, patch) {
    if (!target || !patch) {
        return target
    }
    const fields = captureTimerDateSnapshot(patch)
    if (Object.keys(fields).length === 0) {
        return target
    }

    const working = { ...target, ...fields }
    syncTimerDateStringsFromTimestamps(working)
    const mergedFields = captureTimerDateSnapshot(working)

    const hasChanges = Object.keys(mergedFields).some((key) => target[key] !== mergedFields[key])
    if (!hasChanges) {
        return target
    }

    applyTimerDateSnapshot(target, mergedFields)
    return target
}

/**
 * Al fusionar unidades en movimiento entre canales, conservar la caducidad más restrictiva
 * (date_timestamp más bajo = expira antes).
 */
export function buildChannelMergeDateFields(childData, parentData) {
    const childTs = pickTimestamp(childData, 'date_timestamp')
    const parentTs = pickTimestamp(parentData, 'date_timestamp')

    const useChild = childTs != null && (parentTs == null || childTs <= parentTs)
    if (!useChild || !childData) {
        return {}
    }

    const fields = {}
    Object.keys(childData).forEach((key) => {
        if (isDateRelatedField(key)) {
            fields[key] = childData[key]
        }
    })

    ;['caducidad_estado_inicial_timestamp', 'caducidad_ultimo_estado_timestamp', 'last_move_timestamp'].forEach((key) => {
        if (childData[key] != null && childData[key] !== '') {
            fields[key] = childData[key]
        }
    })

    syncTimerDateStringsFromTimestamps(fields)
    return fields
}
